import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Stack,
  Switch,
  Text,
  Paper,
  Group,
  Box,
  Loader,
  Accordion,
  Divider,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { cohortsApi } from '../../../../services/api/cohorts';
import type { FeatureToggles } from '../../../../services/api/cohorts';
import type {
  SectionController,
  SectionRegister,
} from '../edit-cohort/sectionContract';

interface FeatureTogglesSectionProps {
  cohortId: string;
  /** Called whenever the PENDING toggle values change, so sibling sections
   * (Email Settings lock UI) can reflect what the user is about to save. */
  onToggleChange?: (toggles: FeatureToggles) => void;
  /** MED-1269: register this section's deferred-save controller with the modal. */
  registerSection?: SectionRegister;
}

type AudienceToggle = 'participant_features_enabled' | 'coach_features_enabled';
/** MED-1307: every key the section can hold, including the app access sub-toggle. */
type ToggleKey = keyof FeatureToggles;

const TOGGLE_CONFIG: Record<
  AudienceToggle,
  { label: string; audience: string; facing: string }
> = {
  participant_features_enabled: {
    label: 'Participant Features',
    audience: 'participants',
    facing: 'participant',
  },
  coach_features_enabled: {
    label: 'Coach Features',
    audience: 'coaches',
    facing: 'coach',
  },
};

export function FeatureTogglesSection({
  cohortId,
  onToggleChange,
  registerSection,
}: FeatureTogglesSectionProps) {
  // `saved` is the as-loaded baseline (server truth); `toggles` is pending intent.
  const [saved, setSaved] = useState<FeatureToggles | null>(null);
  const [toggles, setToggles] = useState<FeatureToggles | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchToggles = async () => {
      try {
        const data = await cohortsApi.getFeatureToggles(cohortId);
        setSaved(data);
        setToggles(data);
        onToggleChange?.(data);
      } catch {
        notifications.show({
          title: 'Error',
          message: 'Failed to load feature toggles',
          color: 'red',
        });
      } finally {
        setLoading(false);
      }
    };
    fetchToggles();
    // onToggleChange intentionally omitted — fire once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cohortId]);

  const handleToggleChange = useCallback(
    (toggle: ToggleKey, checked: boolean) => {
      // MED-1269: hold locally; no API call, no per-toggle confirm. Turning OFF
      // is surfaced in the single save-time summary instead (AC11).
      setToggles(prev => {
        if (!prev) return prev;
        const next = { ...prev, [toggle]: checked };
        onToggleChange?.(next);
        return next;
      });
    },
    [onToggleChange]
  );

  // Keep the latest values in refs so the registered controller is stable.
  const togglesRef = useRef(toggles);
  const savedRef = useRef(saved);
  togglesRef.current = toggles;
  savedRef.current = saved;

  const commit = useCallback(async () => {
    const pending = togglesRef.current;
    const base = savedRef.current;
    if (!pending || !base) return;
    const diff: Partial<FeatureToggles> = {};
    (Object.keys(pending) as ToggleKey[]).forEach(k => {
      if (pending[k] !== base[k]) diff[k] = pending[k];
    });
    if (Object.keys(diff).length === 0) return;
    const result = await cohortsApi.updateFeatureToggles(cohortId, diff);
    setSaved(result);
    setToggles(result);
    onToggleChange?.(result);
  }, [cohortId, onToggleChange]);

  useEffect(() => {
    if (!registerSection) return;
    if (!toggles || !saved) return;
    const destructive: SectionController['destructive'] = [];
    (Object.keys(TOGGLE_CONFIG) as AudienceToggle[]).forEach(k => {
      // Turning a feature OFF is the destructive direction (AC11).
      if (saved[k] && !toggles[k]) {
        const cfg = TOGGLE_CONFIG[k];
        destructive.push({
          label: `Turn off ${cfg.label}`,
          details: [
            `SGOS emails to ${cfg.audience} (except calendar invites) will stop`,
            `${cfg.audience} will lose app access for this cohort`,
            `Surveys for ${cfg.audience} will no longer be scheduled`,
          ],
        });
      }
    });
    // MED-1307 (MDF-184): turning participant app access OFF is destructive.
    if (saved.participant_app_access_enabled && !toggles.participant_app_access_enabled) {
      destructive.push({
        label: 'Turn off Participant app access',
        details: [
          'Emails that only lead into the app will stop: peer-leader prep, pre-session reflections, and app invitations',
          'Participants will lose app access for this cohort',
          'Participant emails will continue, without buttons or links into the app',
        ],
      });
    }
    const dirty = (Object.keys(toggles) as ToggleKey[]).some(
      k => toggles[k] !== saved[k]
    );
    registerSection({ dirty, destructive, commit });
    return () => registerSection(null);
  }, [toggles, saved, registerSection, commit]);

  return (
    <Accordion.Item value="feature-toggles">
      <Accordion.Control>
        <Group gap="sm">
          <Text fw={600}>Audience Feature Controls</Text>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        {loading ? (
          <Stack align="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading feature toggles...
            </Text>
          </Stack>
        ) : !toggles ? null : (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Control whether participant and coach features are active for this
              cohort. When disabled, all audience-facing emails (except calendar
              invites) are suppressed, the audience cannot access the app, and
              surveys are not scheduled. Participant app access turns off only
              the app; emails continue. Changes are saved when you click Save
              Changes.
            </Text>

            {(Object.keys(TOGGLE_CONFIG) as AudienceToggle[]).map(toggle => {
              const config = TOGGLE_CONFIG[toggle];
              const isEnabled = toggles[toggle];

              return (
                <Paper key={toggle} p="md" radius="md" withBorder>
                  <Group justify="space-between" align="center" wrap="nowrap">
                    <Box>
                      <Text fw={600}>{config.label}</Text>
                      <Text size="sm" c="dimmed">
                        {isEnabled &&
                        toggle === 'participant_features_enabled' &&
                        toggles.participant_app_access_enabled === false
                          ? 'Participant features are active. App access is off, so participants get emails only.'
                          : isEnabled
                          ? `All ${config.facing}-facing features are active`
                          : `${config.label} are disabled — emails suppressed, app access blocked, surveys paused`}
                      </Text>
                    </Box>
                    <Switch
                      checked={isEnabled}
                      onChange={e =>
                        handleToggleChange(toggle, e.currentTarget.checked)
                      }
                      size="md"
                      color="medleyGreen"
                    />
                  </Group>
                  {toggle === 'participant_features_enabled' && (
                    <AppAccessSubToggle
                      checked={toggles.participant_app_access_enabled ?? true}
                      disabled={!isEnabled}
                      onChange={checked =>
                        handleToggleChange('participant_app_access_enabled', checked)
                      }
                    />
                  )}
                </Paper>
              );
            })}
          </Stack>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
}

/**
 * MED-1307 (MDF-184): "Participant app access" sub-toggle. It sits inside the
 * Participant Features card. It is disabled while Participant Features is off,
 * because the master toggle already blocks the app.
 */
function AppAccessSubToggle({
  checked,
  disabled,
  onChange,
}: {
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  const description = disabled
    ? 'Off while Participant Features is off. Turn Participant Features on to change this.'
    : checked
      ? 'Participants can sign in to the app. Email buttons open the app.'
      : 'Participants see an email-only page in the app. Emails continue, without links into the app.';

  return (
    <>
      <Divider my="sm" />
      <Group justify="space-between" align="center" pl="md" wrap="nowrap">
        <Box>
          <Text fw={500} size="sm" c={disabled ? 'dimmed' : undefined}>
            Participant app access
          </Text>
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        </Box>
        <Switch
          checked={disabled ? false : checked}
          disabled={disabled}
          onChange={e => onChange(e.currentTarget.checked)}
          size="sm"
          color="medleyGreen"
          aria-label="Participant app access"
        />
      </Group>
    </>
  );
}
